import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import { apiData } from "../services/api_service";
import { formatAED } from "../utils/format";
import { formatLocalDateTime } from "../utils/datetime";
import AttachmentLink from "./AttachmentLink";
import PaymentAttachmentUpload from "./PaymentAttachmentUpload";
import { uploadToVercelAPI } from "../config";

const METHOD_OPTIONS = [
  { value: "1", label: "Cash" },
  { value: "2", label: "Card" },
  { value: "3", label: "Cheque" },
  { value: "5", label: "Bank Transfer" },
  { value: "6", label: "Online Payment" },
];

const emptyForm = () => ({
  method: "",
  amount: "",
  referenceNo: "",
  date: dayjs().format("YYYY-MM-DD"),
  attachmentUrl: "",
  attachmentName: "",
  uploading: false,
});

// Everything received against one CDN / Proforma before it is invoiced, and the
// one place payments are recorded or removed: totals up top, every payment with
// its method, reference, proof and who recorded it. The document's creator (or
// an Admin) can add a payment and remove one — a removed payment stays in the
// list, struck through, with who removed it and why. Nothing is hard-deleted.
export default function AdvanceDetailsDialog({ open, onClose, advancesUrl, docNumber, onChanged }) {
  const [state, setState] = useState({ loading: false, data: null });
  const [form, setForm] = useState(null); // null = add form closed
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null); // { advance, reason }
  const [removeBusy, setRemoveBusy] = useState(false);
  const [message, setMessage] = useState(null); // { ok, text }

  const load = useCallback(async () => {
    if (!advancesUrl) return;
    const res = await apiData("get", advancesUrl).catch(() => null);
    setState({ loading: false, data: res?.success ? res.data : null });
  }, [advancesUrl]);

  useEffect(() => {
    if (!open || !advancesUrl) return;
    setState({ loading: true, data: null });
    setForm(null);
    setRemoving(null);
    setMessage(null);
    load();
  }, [open, advancesUrl, load]);

  const d = state.data;
  const rows = d?.advances || [];
  const docOpen = d ? !["converted", "cancelled", "fully_returned"].includes(d.document_status) : false;
  const canAdd = Boolean(d?.can_manage && docOpen && Number(d.balance_due) > 0);
  const amount = parseFloat(form?.amount) || 0;
  const tiles = d
    ? [
        { label: "Grand Total", value: d.document_total, color: "text.primary" },
        { label: "Received", value: d.total_received, color: "success.main" },
        {
          label: "Balance Due",
          value: d.balance_due,
          color: Number(d.balance_due) > 0 ? "warning.dark" : "success.main",
        },
      ]
    : [];

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await apiData("post", advancesUrl, {
        amount,
        payment_method: form.method,
        transaction_id: form.referenceNo,
        payment_date: form.date,
        attachment_url: form.attachmentUrl || null,
        attachment_name: form.attachmentName || null,
      });
      if (res.success) {
        setForm(null);
        setMessage({ ok: true, text: "Payment recorded." });
        await load();
        onChanged?.();
      } else {
        setMessage({ ok: false, text: res.msg || res.message || "Could not record the payment." });
      }
    } catch (e) {
      setMessage({ ok: false, text: e?.response?.data?.message || "Could not record the payment." });
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    setRemoveBusy(true);
    try {
      const res = await apiData("post", `${advancesUrl}/${removing.advance.id}/void`, {
        reason: removing.reason?.trim() || null,
      });
      if (res.success) {
        setRemoving(null);
        setMessage({ ok: true, text: "Payment removed. It stays in the list as removed." });
        await load();
        onChanged?.();
      } else {
        setMessage({ ok: false, text: res.msg || res.message || "Could not remove the payment." });
        setRemoving(null);
      }
    } catch (e) {
      setMessage({ ok: false, text: e?.response?.data?.message || "Could not remove the payment." });
      setRemoving(null);
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <>
      <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              Payments{docNumber ? ` — ${docNumber}` : ""}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 400 }}>
              Money received before this document is invoiced
            </Typography>
          </Box>
          {canAdd && !form && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setForm(emptyForm())}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", mr: 1 }}
            >
              Add payment
            </Button>
          )}
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {state.loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : !d ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Couldn't load the payment details.
            </Typography>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {tiles.map((t) => (
                  <Grid item xs={12} sm={4} key={t.label}>
                    <Box sx={{ border: "1px solid #E3E6EA", borderRadius: "10px", px: 1.5, py: 1 }}>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t.label}</Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: t.color }}>
                        {formatAED(t.value)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {message && (
                <Typography
                  role="status"
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    fontSize: 13,
                    bgcolor: message.ok ? "#ECFDF3" : "#FEF3F2",
                    color: message.ok ? "#027A48" : "#B42318",
                  }}
                >
                  {message.text}
                </Typography>
              )}

              {!d.can_manage && docOpen && (
                <Typography sx={{ mb: 1.5, fontSize: 12.5, color: "text.secondary" }}>
                  Only the person who created this document, or an Admin, can add or remove payments.
                </Typography>
              )}

              {form && (
                <Box sx={{ mb: 2, p: 2, borderRadius: "10px", border: "1px solid #D5DAE0", bgcolor: "#F8FAFC" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>New payment</Typography>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="pay-method">Payment method</InputLabel>
                        <Select
                          labelId="pay-method"
                          label="Payment method"
                          value={form.method}
                          onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
                        >
                          {METHOD_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={`Amount (max ${formatAED(d.balance_due)})`}
                        value={form.amount}
                        inputProps={{ min: 0, step: "0.01" }}
                        onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Payment date"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                        value={form.date}
                        onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Reference no."
                        value={form.referenceNo}
                        onChange={(e) => setForm((f) => ({ ...f, referenceNo: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <PaymentAttachmentUpload
                        height={40}
                        label="Attach payment proof (optional)"
                        url={form.attachmentUrl}
                        name={form.attachmentName}
                        uploading={form.uploading}
                        uploadApi={uploadToVercelAPI}
                        onUploadStart={() => setForm((f) => ({ ...f, uploading: true }))}
                        onUploadSuccess={({ url, name }) =>
                          setForm((f) => ({ ...f, attachmentUrl: url, attachmentName: name, uploading: false }))
                        }
                        onUploadError={(text) => {
                          setForm((f) => ({ ...f, uploading: false }));
                          setMessage({ ok: false, text });
                        }}
                        onRemove={() => setForm((f) => ({ ...f, attachmentUrl: "", attachmentName: "" }))}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Button disabled={saving} onClick={() => setForm(null)} sx={{ textTransform: "none" }}>
                        Cancel
                      </Button>
                      <LoadingButton
                        variant="contained"
                        loading={saving}
                        disabled={!form.method || amount <= 0 || form.uploading}
                        onClick={save}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
                      >
                        Save payment
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box sx={{ border: "1px solid #E3E6EA", borderRadius: "10px", overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontSize: 12, fontWeight: 700, bgcolor: "#F8FAFC" } }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Reference No.</TableCell>
                      <TableCell>Attachment</TableCell>
                      <TableCell>Recorded by</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ "& td": { fontSize: 13 } }}>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 3 }}>
                          No payments received yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((a) => {
                        const removed = a.status === "void";
                        const sx = removed ? { textDecoration: "line-through", color: "text.disabled" } : undefined;
                        return (
                          <TableRow key={a.id} sx={removed ? { "& td": sx } : undefined}>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{dayjs(a.payment_date).format("DD-MM-YYYY")}</TableCell>
                            <TableCell>{a.payment_method || "-"}</TableCell>
                            <TableCell>{a.transaction_id || "-"}</TableCell>
                            <TableCell sx={{ textDecoration: "none !important" }}>
                              <AttachmentLink url={a.attachment_url} name={a.attachment_name} showEmpty />
                            </TableCell>
                            <TableCell>
                              {a.created_by_name || "-"}
                              {removed && (
                                <Typography sx={{ fontSize: 11, color: "error.main", textDecoration: "none" }}>
                                  Removed by {a.voided_by_name || "-"}
                                  {a.voided_at ? ` · ${formatLocalDateTime(a.voided_at)}` : ""}
                                  {a.void_reason ? ` — ${a.void_reason}` : ""}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                              {formatAED(a.amount)}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: "nowrap", textDecoration: "none !important" }}>
                              {!removed && a.can_delete && (
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                                  onClick={() => setRemoving({ advance: a, reason: "" })}
                                  sx={{ textTransform: "none", fontWeight: 600 }}
                                >
                                  Remove
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="contained" sx={{ textTransform: "none", borderRadius: "8px" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(removing)}
        onClose={removeBusy ? undefined : () => setRemoving(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>Remove this payment?</DialogTitle>
        <DialogContent>
          {removing && (
            <>
              <Typography sx={{ fontSize: 14, color: "#334155", mb: 1.5 }}>
                {formatAED(removing.advance.amount)} · {removing.advance.payment_method} ·{" "}
                {dayjs(removing.advance.payment_date).format("DD-MM-YYYY")}. It stays in the list, marked as removed,
                with your name and the reason.
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Reason (optional)"
                value={removing.reason}
                onChange={(e) => setRemoving((r) => ({ ...r, reason: e.target.value }))}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRemoving(null)} disabled={removeBusy} color="inherit" sx={{ textTransform: "none" }}>
            Keep it
          </Button>
          <LoadingButton
            onClick={confirmRemove}
            loading={removeBusy}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            Remove payment
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
